import React, { useEffect } from 'react';
import { X, Copy, Download, RefreshCw } from 'lucide-react';
import { useWorkflowMetadata } from '../contexts/WorkflowMetadataContext';
import { useWorkflowMetadataFetch } from '../hooks/useWorkflowMetadataFetch';
import './MetadataModal.css';

interface MetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  contextId?: string | null;
  scriptId?: string | null;
  currentPhase?: number;
}

interface MetadataSection {
  title: string;
  items: Array<{
    label: string;
    value: string | number | boolean | null | undefined;
    copyable?: boolean;
    type?: 'id' | 'time' | 'text' | 'boolean' | 'number';
  }>;
}

export const MetadataModal: React.FC<MetadataModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  contextId,
  scriptId,
  currentPhase
}) => {
  const { metadata, clearMetadata } = useWorkflowMetadata();
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Fetch metadata when modal opens and contextId is available
  const { fetchMetadata, refetch } = useWorkflowMetadataFetch(contextId || undefined, {
    enabled: isOpen && !!contextId,
    pollingInterval: 0, // No auto-polling for modal
    enablePolling: false
  });

  // Clear metadata when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearMetadata();
    }
  }, [isOpen, clearMetadata]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(label);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatValue = (value: any, type?: string): string => {
    if (value === null || value === undefined) {
      return '—';
    }

    switch (type) {
      case 'time':
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? '✓' : '✗';
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      case 'id':
        return value.length > 12 ? `${value.substring(0, 12)}...` : value;
      default:
        return String(value);
    }
  };

  const renderSection = (section: MetadataSection) => (
    <div className="metadata-section" key={section.title}>
      <h3 className="metadata-section-title">{section.title}</h3>
      <div className="metadata-items">
        {section.items.map((item, index) => (
          <div className="metadata-item" key={index}>
            <span className="metadata-label">{item.label}:</span>
            <span className={`metadata-value ${item.type === 'id' ? 'metadata-id' : ''}`}>
              {formatValue(item.value, item.type)}
              {item.copyable && item.value && (
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(String(item.value), item.label)}
                  title="Copy to clipboard"
                >
                  {copiedId === item.label ? '✓' : <Copy size={14} />}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const sections: MetadataSection[] = [];

  // Build sections from metadata
  if (metadata?.session || sessionId) {
    sections.push({
      title: 'Session Information',
      items: [
        { label: 'Session ID', value: sessionId || metadata?.session?.id, copyable: true, type: 'id' },
        { label: 'Started', value: metadata?.session?.startedAt, type: 'time' },
        { label: 'User', value: metadata?.session?.userId, type: 'text' },
        { label: 'Tenant', value: metadata?.session?.tenantId, type: 'text' }
      ]
    });
  }

  if (metadata?.workflow) {
    sections.push({
      title: 'Workflow Details',
      items: [
        { label: 'Context ID', value: metadata.workflow.contextId, copyable: true, type: 'id' },
        { label: 'Workflow ID', value: metadata.workflow.workflowId, copyable: true, type: 'id' },
        { label: 'Script ID', value: metadata.workflow.scriptId, copyable: true, type: 'id' },
        { label: 'Phase', value: metadata.workflow.phase, type: 'number' },
        { label: 'Status', value: metadata.workflow.status, type: 'text' },
        // These fields might not exist - use type assertion
        ...((metadata.workflow as any).startedAt ? [{ label: 'Started At', value: (metadata.workflow as any).startedAt, type: 'time' as const }] : []),
        ...((metadata.workflow as any).interruptedAt ? [{ label: 'Interrupted At', value: (metadata.workflow as any).interruptedAt, type: 'time' as const }] : [])
      ]
    });
  }

  if (metadata?.taskIdentification) {
    sections.push({
      title: 'Task Identification',
      items: [
        { label: 'Domain', value: metadata.taskIdentification.domain, type: 'text' },
        { label: 'Task', value: metadata.taskIdentification.task, type: 'text' },
        { label: 'Method', value: metadata.taskIdentification.method, type: 'text' },
        // Confidence may not exist - use type assertion
        ...((metadata.taskIdentification as any).confidence ? [{ label: 'Confidence', value: (metadata.taskIdentification as any).confidence, type: 'text' as const }] : []),
        { label: 'Identified At', value: metadata.taskIdentification.identifiedAt, type: 'time' },
        { label: 'Response Time', value: metadata.taskIdentification.responseTimeMs ? `${metadata.taskIdentification.responseTimeMs}ms` : '—', type: 'text' },
        { label: 'API Calls', value: metadata.taskIdentification.apiCalls, type: 'number' },
        { label: 'Cache Hit', value: metadata.taskIdentification.cacheHit, type: 'boolean' }
      ]
    });
  }

  if (metadata?.knowledgeBase) {
    sections.push({
      title: 'Knowledge Base',
      items: [
        { label: 'Framework', value: metadata.knowledgeBase.framework, type: 'text' },
        { label: 'Domain Rules', value: metadata.knowledgeBase.domainRules, type: 'text' },
        { label: 'Safety Checks', value: metadata.knowledgeBase.safetyChecks, type: 'boolean' }
      ]
    });
  }

  if (metadata?.performance) {
    sections.push({
      title: 'Performance Metrics',
      items: [
        { label: 'Total Latency', value: metadata.performance.totalLatencyMs ? `${metadata.performance.totalLatencyMs}ms` : '—', type: 'text' },
        // These performance fields might not exist - use type assertion
        ...((metadata.performance as any).tokensUsed ? [{ label: 'Tokens Used', value: (metadata.performance as any).tokensUsed, type: 'number' as const }] : []),
        ...((metadata.performance as any).cacheHits ? [{ label: 'Cache Hits', value: (metadata.performance as any).cacheHits, type: 'number' as const }] : []),
        ...((metadata.performance as any).cacheHitRate ? [{ label: 'Cache Hit Rate', value: `${((metadata.performance as any).cacheHitRate * 100).toFixed(1)}%`, type: 'text' as const }] : [])
      ]
    });
  }
  
  // These sections may not exist in the metadata structure
  // Add them dynamically if they exist
  
  // Audit section
  if (metadata?.audit) {
    sections.push({
      title: 'Audit Information',
      items: [
        { label: 'Created By', value: metadata.audit.createdBy, type: 'text' },
        { label: 'Created At', value: metadata.audit.createdAt, type: 'time' },
        { label: 'Last Modified', value: metadata.audit.lastModified, type: 'time' },
        { label: 'Version', value: metadata.audit.version, type: 'number' }
      ]
    });
  }

  const exportMetadata = () => {
    const dataStr = JSON.stringify(metadata, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `metadata_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const copyAllMetadata = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
      setCopiedId('all');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy metadata:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="metadata-modal-overlay" onClick={onClose}>
      <div className="metadata-modal" onClick={e => e.stopPropagation()}>
        <div className="metadata-modal-header">
          <h2 className="metadata-modal-title">Workflow Metadata</h2>
          <div className="metadata-modal-actions">
            <button
              className={`metadata-action-btn ${isRefreshing ? 'refreshing' : ''}`}
              onClick={handleRefresh}
              title="Refresh metadata"
              disabled={!contextId}
            >
              <RefreshCw size={16} className={isRefreshing ? 'spin' : ''} />
            </button>
            <button 
              className="metadata-action-btn"
              onClick={copyAllMetadata}
              title="Copy all metadata"
            >
              {copiedId === 'all' ? '✓' : <Copy size={16} />}
            </button>
            <button 
              className="metadata-action-btn"
              onClick={exportMetadata}
              title="Export as JSON"
            >
              <Download size={16} />
            </button>
            <button
              className="metadata-modal-close"
              onClick={onClose}
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="metadata-modal-content">
          {contextId ? (
            sections.length > 0 ? (
              sections.map(section => renderSection(section))
            ) : (
              <div className="metadata-empty">
                <p>Loading metadata...</p>
              </div>
            )
          ) : (
            <div className="metadata-empty">
              <p>No workflow context available. Start a workflow to see metadata.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};